import React, { useRef, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { Baseurl } from '../../../Api/Baseurl';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { ToastOptions } from '../../../helpers/ToastOptions';
import { MdCheckCircle, MdCancel, MdVisibility } from "react-icons/md";
import { FaSpinner } from "react-icons/fa";
import Swal from 'sweetalert2';
import './Views.scss';

export default function Views() {
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [actionType, setActionType] = useState('');
    const [statusFilter, setStatusFilter] = useState('pending');
    const token = localStorage.getItem('medtoken');
    const queryClient = useQueryClient();
    const animationRef = useRef();
    const animationInView = useInView(animationRef, { once: true });

    // Ensure modal is closed and table is visible on mount/reload
    useEffect(() => {
        setShowDetails(false);
        setSelectedRequest(null);
    }, []);

    // Defensive: if modal is open but no selectedRequest, close modal
    useEffect(() => {
        if (showDetails && !selectedRequest) {
            setShowDetails(false);
        }
    }, [showDetails, selectedRequest]);

    // Fetch view requests
    const { data: viewRequests, isLoading, error, refetch } = useQuery(
        ['viewRequests', statusFilter],
        async () => {
            const response = await axios.get(`${Baseurl}/admin/view-requests?status=${statusFilter}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.data;
        },
        {
            refetchInterval: 30000, // Refetch every 30 seconds
            retry: 3,
            onError: (error) => {
                toast.error("Failed to load view requests", ToastOptions("error"));
            }
        }
    );

    console.log('Component State:', { viewRequests, isLoading, error });
    console.log('Modal State:', { showDetails, selectedRequest, showRejectionModal });

    // Approve view request mutation
    const approveRequestMutation = useMutation(
        async (requestId) => {
            const response = await axios.post(
                `${Baseurl}/admin/view-requests/${requestId}/approve`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            return response.data;
        },
        {
            onSuccess: (data) => {
                toast.success(data.msg, ToastOptions("success"));
                queryClient.invalidateQueries(['viewRequests', statusFilter]);
                setShowDetails(false);
                setSelectedRequest(null);
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || "Failed to approve request", ToastOptions("error"));
            }
        }
    );

    // Reject view request mutation
    const rejectRequestMutation = useMutation(
        async ({ requestId, reason }) => {
            const response = await axios.post(
                `${Baseurl}/admin/view-requests/${requestId}/reject`,
                { reason },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            return response.data;
        },
        {
            onSuccess: (data) => {
                toast.success(data.msg, ToastOptions("success"));
                queryClient.invalidateQueries(['viewRequests', statusFilter]);
                setShowDetails(false);
                setShowRejectionModal(false);
                setSelectedRequest(null);
                setRejectionReason('');
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || "Failed to reject request", ToastOptions("error"));
            }
        }
    );

    const handleStatusUpdate = (request, status) => {
        setSelectedRequest(request);
        setActionType(status);

        if (status === 'rejected') {
            setShowRejectionModal(true);
        } else {
            Swal.fire({
                title: `Confirm ${status}`,
                text: `Are you sure you want to ${status} this view request?`,
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#01677e",
                cancelButtonColor: "#d33",
                confirmButtonText: `Yes, ${status} it!`
            }).then((result) => {
                if (result.isConfirmed) {
                    approveRequestMutation.mutate(request.id);
                }
            });
        }
    };

    const handleRejectionSubmit = () => {
        if (!rejectionReason.trim()) {
            toast.error("Please provide a rejection reason", ToastOptions("error"));
            return;
        }

        rejectRequestMutation.mutate({
            requestId: selectedRequest.id,
            reason: rejectionReason.trim()
        });
    };

    const openDetails = (request) => {
        setSelectedRequest(request);
        setShowDetails(true);
    };

    const closeDetails = () => {
        setShowDetails(false);
        setSelectedRequest(null);
    };

    const closeRejectionModal = () => {
        setShowRejectionModal(false);
        setRejectionReason('');
        setSelectedRequest(null);
    };

    const getStatusCount = (status) => {
        if (!viewRequests?.payload?.counts) return 0;
        return viewRequests.payload.counts[status] || 0;
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <FaSpinner className="animate-spin text-4xl text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading View Requests</h2>
                    <p className="text-gray-600 mb-4">Failed to load view requests. Please try again.</p>
                    <button 
                        onClick={() => refetch()}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Table and header always visible */}
            <div className="views-container">
                <div className="header-section">
                    <h1 className="page-title">View Requests Management</h1>
                    <div className="stats">
                        <div className="stat-card">
                            <span className="stat-number">{getStatusCount('pending')}</span>
                            <span className="stat-label">Pending Requests</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-number">{getStatusCount('approved')}</span>
                            <span className="stat-label">Approved Requests</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-number">{getStatusCount('rejected')}</span>
                            <span className="stat-label">Rejected Requests</span>
                        </div>
                        <div className="stat-card total-card">
                            <span className="stat-number">{getStatusCount('total')}</span>
                            <span className="stat-label">Total Requests</span>
                        </div>
                    </div>
                    
                    <div className="filter-section">
                        <label className="filter-label">Filter by Status:</label>
                        <select 
                            value={statusFilter} 
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="">All</option>
                        </select>
                    </div>
                </div>

                <div className='main-Orders'>
                    <div className="overflow-x-auto shadow-md sm:rounded-lg">
                        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                <tr>
                                    <th scope="col" className="px-6 py-3">Student Name</th>
                                    <th scope="col" className="px-6 py-3">Course</th>
                                    <th scope="col" className="px-6 py-3">Lecture</th>
                                    <th scope="col" className="px-6 py-3">Status</th>
                                    <th scope="col" className="px-6 py-3">Request Date</th>
                                    <th scope="col" className="px-6 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(viewRequests?.payload?.requests) && viewRequests.payload.requests.length > 0 ? (
                                    viewRequests.payload.requests.map((request, index) => (
                                        <tr 
                                            key={request.id} 
                                            className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                                        >
                                            <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                                {request.student?.name || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4">
                                                {request.course?.name || 'N/A'}
                                                {request.course?.grade && (
                                                    <span className="text-sm text-gray-500 ml-2">
                                                        (Grade {request.course.grade})
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {request.lecture?.title || 'N/A'}
                                                {request.lecture?.lecNumber && (
                                                    <span className="text-sm text-gray-500 ml-2">
                                                        (Lec {request.lecture.lecNumber})
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`status-badge ${request.status}`}>
                                                    {request.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {new Date(request.createdAt).toLocaleDateString('en-GB', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </td>
                                            <td className="px-6 py-4 svg-del">
                                                <span>
                                                    <MdVisibility 
                                                        onClick={() => openDetails(request)}
                                                        title="View Details"
                                                        className="action-icon view-icon"
                                                    />
                                                </span>
                                                {request.status === 'pending' && (
                                                    <>
                                                        <span>
                                                            <MdCheckCircle 
                                                                onClick={() => handleStatusUpdate(request, 'approved')}
                                                                title="Approve Request"
                                                                className="action-icon approve-icon"
                                                            />
                                                        </span>
                                                        <span>
                                                            <MdCancel 
                                                                onClick={() => handleStatusUpdate(request, 'rejected')}
                                                                title="Reject Request"
                                                                className="action-icon reject-icon"
                                                            />
                                                        </span>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-8 text-gray-500">
                                            {Array.isArray(viewRequests?.payload?.requests)
                                                ? 'No view requests found.'
                                                : 'No data or data format error. See debug above.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal as sibling, overlays page, table always visible */}
            <AnimatePresence>
            {showDetails && selectedRequest && (
                <motion.div 
                    className="back-drop" 
                    onClick={closeDetails}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <motion.div 
                        className="popup apperdetails"
                        style={{ maxHeight: '90vh', overflowY: 'auto', width: '95vw', maxWidth: 600, padding: 0 }}
                        onClick={e => e.stopPropagation()}
                        initial={{ opacity: 0, y: 60 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 60 }}
                        transition={{ duration: 0.25, type: "spring", stiffness: 300, damping: 30 }}
                    >
                        <button className="close-btn" onClick={closeDetails} title="Close">&times;</button>
                        <div className="profile" style={{ padding: 32 }}>
                            <motion.div 
                                className="request-details"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1, duration: 0.2 }}
                            >
                                <motion.h2 
                                    className="details-title"
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2, duration: 0.3 }}
                                >
                                    View Request Details
                                </motion.h2>
                                
                                <motion.div 
                                    className="student-info"
                                    initial={{ opacity: 0, x: -30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.3, duration: 0.3 }}
                                >
                                    <h3 className="section-title">Student Information</h3>
                                    <div className="info-grid">
                                        <motion.div 
                                            className="info-item"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.4, duration: 0.2 }}
                                        >
                                            <label>Name:</label>
                                            <span>{selectedRequest.student?.name || 'N/A'}</span>
                                        </motion.div>
                                        <motion.div 
                                            className="info-item"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.5, duration: 0.2 }}
                                        >
                                            <label>Email:</label>
                                            <span>{selectedRequest.student?.email || 'N/A'}</span>
                                        </motion.div>
                                        <motion.div 
                                            className="info-item"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.6, duration: 0.2 }}
                                        >
                                            <label>Mobile:</label>
                                            <span>{selectedRequest.student?.mobileNo || 'N/A'}</span>
                                        </motion.div>
                                        <motion.div 
                                            className="info-item"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.7, duration: 0.2 }}
                                        >
                                            <label>Status:</label>
                                            <span className={`status-badge ${selectedRequest.status}`}>
                                                {selectedRequest.status}
                                            </span>
                                        </motion.div>
                                    </div>
                                </motion.div>

                                <motion.div 
                                    className="course-info"
                                    initial={{ opacity: 0, x: 30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.4, duration: 0.3 }}
                                >
                                    <h3 className="section-title">Course Information</h3>
                                    <div className="info-grid">
                                        <motion.div 
                                            className="info-item"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.5, duration: 0.2 }}
                                        >
                                            <label>Course Name:</label>
                                            <span>{selectedRequest.course?.name || 'N/A'}</span>
                                        </motion.div>
                                        <motion.div 
                                            className="info-item"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.6, duration: 0.2 }}
                                        >
                                            <label>Grade:</label>
                                            <span>{selectedRequest.course?.grade || 'N/A'}</span>
                                        </motion.div>
                                        <motion.div 
                                            className="info-item"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.7, duration: 0.2 }}
                                        >
                                            <label>Lecture Title:</label>
                                            <span>{selectedRequest.lecture?.title || 'N/A'}</span>
                                        </motion.div>
                                        <motion.div 
                                            className="info-item"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.8, duration: 0.2 }}
                                        >
                                            <label>Lecture Number:</label>
                                            <span>{selectedRequest.lecture?.lecNumber || 'N/A'}</span>
                                        </motion.div>
                                        <motion.div 
                                            className="info-item"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.9, duration: 0.2 }}
                                        >
                                            <label>Request ID:</label>
                                            <span className="request-id">{selectedRequest.id}</span>
                                        </motion.div>
                                        <motion.div 
                                            className="info-item"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 1.0, duration: 0.2 }}
                                        >
                                            <label>Requested:</label>
                                            <span>
                                                {new Date(selectedRequest.createdAt).toLocaleDateString('en-GB', {
                                                    day: '2-digit',
                                                    month: 'long',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </span>
                                        </motion.div>
                                        {selectedRequest.updatedAt && selectedRequest.updatedAt !== selectedRequest.createdAt && (
                                            <motion.div 
                                                className="info-item"
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 1.1, duration: 0.2 }}
                                            >
                                                <label>Last Updated:</label>
                                                <span>
                                                    {new Date(selectedRequest.updatedAt).toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: 'long',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                            </motion.div>
                                        )}
                                    </div>
                                </motion.div>

                                {selectedRequest.status === 'pending' && (
                                    <motion.div 
                                        className="action-buttons"
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 1.3, duration: 0.3 }}
                                    >
                                        <motion.button 
                                            onClick={() => handleStatusUpdate(selectedRequest, 'approved')}
                                            className="btn-approve"
                                            disabled={approveRequestMutation.isLoading || rejectRequestMutation.isLoading}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            {approveRequestMutation.isLoading ? <FaSpinner className="animate-spin" /> : <MdCheckCircle />}
                                            Approve
                                        </motion.button>
                                        <motion.button 
                                            onClick={() => handleStatusUpdate(selectedRequest, 'rejected')}
                                            className="btn-reject"
                                            disabled={approveRequestMutation.isLoading || rejectRequestMutation.isLoading}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            {rejectRequestMutation.isLoading ? <FaSpinner className="animate-spin" /> : <MdCancel />}
                                            Reject
                                        </motion.button>
                                        <motion.button 
                                            onClick={closeDetails}
                                            className="btn-close"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            Close
                                        </motion.button>
                                    </motion.div>
                                )}

                                {selectedRequest.status !== 'pending' && (
                                    <motion.div 
                                        className="action-buttons"
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 1.3, duration: 0.3 }}
                                    >
                                        <motion.button 
                                            onClick={closeDetails}
                                            className="btn-close"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            Close
                                        </motion.button>
                                    </motion.div>
                                )}
                            </motion.div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
            </AnimatePresence>

            {/* Rejection Modal - same style/animation as above */}
            <AnimatePresence>
            {showRejectionModal && (
                <motion.div 
                    className="back-drop" 
                    onClick={closeRejectionModal}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <motion.div 
                        className="popup apperdetails rejection-modal"
                        style={{ maxHeight: '90vh', overflowY: 'auto', width: '95vw', maxWidth: 500, padding: 0 }}
                        onClick={e => e.stopPropagation()}
                        initial={{ opacity: 0, y: 60 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 60 }}
                        transition={{ duration: 0.25, type: "spring", stiffness: 300, damping: 30 }}
                    >
                        <button className="close-btn" onClick={closeRejectionModal} title="Close">&times;</button>
                        <div className="profile" style={{ padding: 32 }}>
                            <motion.div 
                                className="rejection-form"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1, duration: 0.2 }}
                            >
                                <motion.h2 
                                    className="modal-title"
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2, duration: 0.3 }}
                                >
                                    Reject View Request
                                </motion.h2>
                                <motion.p 
                                    className="modal-subtitle"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3, duration: 0.3 }}
                                >
                                    Please provide a reason for rejecting this view request:
                                </motion.p>
                                
                                <motion.div 
                                    className="form-group"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4, duration: 0.3 }}
                                >
                                    <label htmlFor="rejectionReason" className="form-label">Rejection Reason *</label>
                                    <motion.textarea
                                        id="rejectionReason"
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder="Enter the reason for rejection..."
                                        className="form-textarea"
                                        rows="4"
                                        required
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.5, duration: 0.3 }}
                                        whileFocus={{ scale: 1.02 }}
                                    />
                                </motion.div>

                                <motion.div 
                                    className="modal-actions"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6, duration: 0.3 }}
                                >
                                    <motion.button 
                                        onClick={handleRejectionSubmit}
                                        className="btn-submit"
                                        disabled={rejectRequestMutation.isLoading || !rejectionReason.trim()}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        {rejectRequestMutation.isLoading ? <FaSpinner className="animate-spin" /> : 'Submit'}
                                    </motion.button>
                                    <motion.button 
                                        onClick={closeRejectionModal}
                                        className="btn-cancel"
                                        disabled={rejectRequestMutation.isLoading}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        Cancel
                                    </motion.button>
                                </motion.div>
                            </motion.div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
            </AnimatePresence>
        </>
    );
}
